import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { interpretarNumero } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { useGravarFaixa } from './consultas-frota';
import type { EquipamentoDaFrota } from './frota';
import { MensagemDeErro } from './MensagemDeErro';

/**
 * A faixa de consumo DESTA máquina — decisão 8 do dono: "isso deve ser atrelado
 * ao cadastro do equipamento". Preenchida, ela vence a da configuração; os dois
 * campos em branco voltam a herdar.
 *
 * Só aparece para quem pode gerir o equipamento: um campo que o servidor
 * recusaria seria prometer o que o perfil não pode.
 */
export function FormularioFaixa({ equipamento }: { readonly equipamento: EquipamentoDaFrota }) {
  const base = useId();
  const sessao = useSessao();
  const gravar = useGravarFaixa();
  const daUnidade = equipamento.origemDaFaixa === 'unidade';
  const [minimo, setMinimo] = useState(daUnidade && equipamento.consumoMinimo !== null ? String(equipamento.consumoMinimo).replace('.', ',') : '');
  const [maximo, setMaximo] = useState(daUnidade && equipamento.consumoMaximo !== null ? String(equipamento.consumoMaximo).replace('.', ',') : '');
  /* UM ERRO POR CAMPO, calculado AO SAIR dele (p.16; R01, p.28). A faixa
     invertida aparece no campo de onde a pessoa acabou de sair — e nao sempre
     embaixo do minimo, como quando havia um erro so. */
  const [erroDoMinimo, setErroDoMinimo] = useState<string | null>(null);
  const [erroDoMaximo, setErroDoMaximo] = useState<string | null>(null);
  /* AJUSTAR A FAIXA E RARO (pedido do dono, 31/10/2026): os campos ficam atras
     do botao, como "Corrigir a leitura" e o cupom do lancamento. O foco vai e
     volta com eles, para quem esta no teclado nao cair no topo do painel. */
  const [aberto, setAberto] = useState(false);
  const campoDoMinimo = useRef<HTMLInputElement>(null);
  const botaoDeAjustar = useRef<HTMLButtonElement>(null);
  const trocouAgora = useRef(false);
  useEffect(() => {
    if (!trocouAgora.current) return;
    trocouAgora.current = false;
    (aberto ? campoDoMinimo.current : botaoDeAjustar.current)?.focus();
  }, [aberto]);

  if (!(sessao.data?.permissoes ?? []).includes('equipamento.gerir')) return null;
  const unidade = equipamento.tipoMedidor === 'odometro' ? 'L/km' : 'L/h';

  const numeroOuNulo = (texto: string) => (texto.trim() === '' ? null : interpretarNumero(texto));

  /** Os erros dos dois campos, dado de qual deles a pessoa saiu. */
  function conferir(saiuDe: 'minimo' | 'maximo' | 'envio') {
    const min = numeroOuNulo(minimo);
    const max = numeroOuNulo(maximo);
    const formatoDoMinimo = minimo.trim() !== '' && min === null ? 'Use números, como 14 ou 14,5.' : null;
    const formatoDoMaximo = maximo.trim() !== '' && max === null ? 'Use números, como 14 ou 14,5.' : null;
    const invertida = min !== null && max !== null && min > max;
    return {
      minimo: formatoDoMinimo ?? (invertida && saiuDe !== 'maximo' ? 'O mínimo não pode ser maior que o máximo.' : null),
      maximo: formatoDoMaximo ?? (invertida && saiuDe === 'maximo' ? 'O máximo não pode ser menor que o mínimo.' : null),
    };
  }

  function mostrar(saiuDe: 'minimo' | 'maximo' | 'envio') {
    const erros = conferir(saiuDe);
    setErroDoMinimo(erros.minimo);
    setErroDoMaximo(erros.maximo);
    return erros.minimo === null && erros.maximo === null;
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!mostrar('envio')) return;
    gravar.mutate(
      { id: equipamento.id, minimo: numeroOuNulo(minimo), maximo: numeroOuNulo(maximo) },
      {
        onSuccess: () => {
          trocouAgora.current = true;
          setAberto(false);
        },
      },
    );
  }

  if (!aberto) {
    return (
      <div className="flex">
        <button
          ref={botaoDeAjustar}
          type="button"
          className="botao botao-fantasma botao-p"
          aria-expanded={false}
          aria-controls={`${base}-faixa`}
          onClick={() => {
            trocouAgora.current = true;
            setAberto(true);
          }}
        >
          <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
          Ajustar a faixa desta máquina
        </button>
      </div>
    );
  }

  return (
    <form id={`${base}-faixa`} onSubmit={enviar} className="flex flex-col gap-2">
      <div className="flex">
        <Botao
          nivel="fantasma"
          tamanho="p"
          aria-expanded
          aria-controls={`${base}-faixa`}
          onClick={() => {
            trocouAgora.current = true;
            setAberto(false);
            setErroDoMinimo(null);
            setErroDoMaximo(null);
          }}
        >
          <ChevronUp aria-hidden="true" className="size-[var(--icone-controle)]" />
          Fechar o ajuste da faixa
        </Botao>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <Campo id={`${base}-min`} rotulo={`Mínimo (${unidade})`} opcional erro={erroDoMinimo}>
          <input
            ref={campoDoMinimo}
            id={`${base}-min`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full"
            value={minimo}
            onChange={(e) => {
              setErroDoMinimo(null);
              setMinimo(e.target.value);
            }}
            onBlur={() => {
              mostrar('minimo');
            }}
            {...(erroDoMinimo ? { 'aria-invalid': true, 'aria-describedby': `${base}-min-erro` } : {})}
          />
        </Campo>
        <Campo id={`${base}-max`} rotulo={`Máximo (${unidade})`} opcional erro={erroDoMaximo}>
          <input
            id={`${base}-max`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full"
            value={maximo}
            onChange={(e) => {
              setErroDoMaximo(null);
              setMaximo(e.target.value);
            }}
            onBlur={() => {
              mostrar('maximo');
            }}
            {...(erroDoMaximo ? { 'aria-invalid': true, 'aria-describedby': `${base}-max-erro` } : {})}
          />
        </Campo>
        <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
          Gravar faixa
        </Botao>
      </div>
      <span className="txt-apoio text-tinta-apagada">
        Os dois em branco voltam a usar a faixa da configuração.
      </span>
      <MensagemDeErro erro={gravar.error} />
    </form>
  );
}
